import {businessCalendarDate, businessTimestampFromDate} from 'src/api/business-time'

import type {RentalAsset} from '../assets/rental-assets/schemas'
import type {Worker} from '../assets/workers/schemas'
import {commitJobToTimeline, isJobDispatched} from '../dispatch-timeline/allocation'
import type {
    AllocationStatus,
    BoardJobIdentity,
    DispatchAssignment,
    DispatchResource,
    DispatchTimelineData,
    JobAllocation,
    JobRequirement,
    UnassignedJob,
} from '../dispatch-timeline/types'
import {buildWorkDaySlots} from '../dispatch-timeline/work-day'
import {parseApiDate} from '../jobs/job-schedule-format'
import {outOfStateLabel} from '../lib/texas-bounds'
import type {DispatchBoardScenario} from './dispatch-board-scenario'
import {buildTodayScheduleDate, deriveInitials, formatJobCode} from './dispatch-mapper'
import {mapJobScheduleToTimeline} from './job-schedule-to-timeline'
import type {DispatchBoardJob, DispatchBoardResponse} from './schemas'

function mapWorkerToResource(worker: Worker): DispatchResource {
    return {
        id: worker.id,
        name: worker.name,
        initials: deriveInitials(worker.name),
        workerTypeId: worker.workerTypeId,
        homeYardId: worker.homeDispatchYardId ?? null,
        ...(worker.isPlaceholder ? {isPlaceholder: true} : {}),
    }
}

function mapUniqueRentalAssetToResource(asset: RentalAsset): DispatchResource {
    const name = asset.name ?? asset.rentalAssetTypeName
    return {
        id: asset.id,
        name,
        initials: deriveInitials(name),
        rentalAssetTypeId: asset.rentalAssetTypeId,
        rentalAssetTypeName: asset.rentalAssetTypeName,
        isUniquelyIdentified: asset.isUniquelyIdentified,
    }
}

function boardScenarioFromBoardJob(job: DispatchBoardJob): DispatchBoardScenario | undefined {
    const projectName = job.projectName
    const siteName = job.locations[0]?.name
    const description = job.serviceTypeName.trim() || undefined

    if (!projectName && !siteName && !description) {
        return undefined
    }

    return {projectName, siteName, description}
}

function buildOutOfStateLabelProps(job: DispatchBoardJob): {outOfStateLabel?: string} {
    const label = job.locations.map((location) => outOfStateLabel(location)).find(Boolean)
    return label ? {outOfStateLabel: label} : {}
}

export function boardJobIdentity(job: DispatchBoardJob): BoardJobIdentity {
    return {
        description: job.description,
        serviceTypeName: job.serviceTypeName,
        projectId: job.projectId,
        projectName: job.projectName,
        repName: job.internalContactName,
        repId: job.repId ?? null,
        projectCreatedById: job.projectCreatedById ?? null,
        createdById: job.createdById ?? null,
        customerId: job.customerId,
        customerName: job.customerName,
        dispatchYardId: job.dispatchYardId,
        dispatchYardName: job.dispatchYardName,
        closerYardName: job.closerYardName,
        closerYardReason: job.closerYardReason,
        ...buildOutOfStateLabelProps(job),
        bookByDay: job.bookByDay,
        isPickup: job.isPickup,
        ...(job.partialPickup ? {partialPickup: job.partialPickup} : {}),
        didNotHappenAt: job.didNotHappenAt,
        pickupConfirmedAt: job.pickupConfirmedAt,
        unconfirmedRentals: job.unconfirmedRentals,
        driverOwnedTruckTypes: job.driverOwnedTruckTypes,
        onSiteEquipmentTypes: job.onSiteEquipmentTypes,
        dateTbc: job.dateTbc,
        dateTbcDayCount: job.dateTbcDayCount,
        displayId: job.displayId ?? formatJobCode(job.id),
        siteName: job.locations[0]?.name ?? null,
        startDate: job.startDate,
        startTime: job.startTime,
        endDate: job.endDate,
        endTime: job.endTime,
    }
}

function mapBoardRequirement(
    requirement:
        | DispatchBoardJob['workerRequirements'][number]
        | DispatchBoardJob['assetRequirements'][number],
    kind: JobRequirement['kind'],
): JobRequirement {
    if (kind === 'worker') {
        const workerRequirement = requirement as DispatchBoardJob['workerRequirements'][number]
        return {
            id: workerRequirement.id,
            kind,
            label: workerRequirement.workerTypeName,
            quantity: workerRequirement.quantity,
            assignedQuantity: workerRequirement.assignedQuantity,
            workerTypeId: workerRequirement.workerTypeId,
            isDriver: workerRequirement.isDriver,
        }
    }

    const assetRequirement = requirement as DispatchBoardJob['assetRequirements'][number]
    return {
        id: assetRequirement.id,
        kind,
        label: assetRequirement.rentalAssetTypeName,
        quantity: assetRequirement.quantity,
        rentalAssetTypeId: assetRequirement.rentalAssetTypeId,
        assignedQuantity: assetRequirement.assignedQuantity,
        ...(assetRequirement.onSiteQuantity
            ? {onSiteQuantity: assetRequirement.onSiteQuantity}
            : {}),
    }
}

function mapBoardJobToAllocations(job: DispatchBoardJob): JobAllocation[] {
    const allocations: JobAllocation[] = []
    const workerAssignments = [...job.workerAssignments]

    // Map assignments up to each requirement's quantity, then any remainder.
    const workerRequirements = job.workerRequirements
    const takeAssignmentsForRequirement = (
        requirement: (typeof workerRequirements)[number],
        limit: number,
    ) => {
        const matching = workerAssignments
            .filter((assignment) => assignment.workerTypeId === requirement.workerTypeId)
            .slice(0, limit)
        for (const assignment of matching) {
            workerAssignments.splice(workerAssignments.indexOf(assignment), 1)
            allocations.push({
                requirementId: requirement.id,
                resourceId: assignment.workerId,
                resourceName: assignment.workerName,
                kind: 'worker',
                assignmentId: assignment.id,
                ...(assignment.notifiedAt != null ? {notifiedAt: assignment.notifiedAt} : {}),
                ...(assignment.isPlaceholder ? {isPlaceholder: true} : {}),
                ...(assignment.startDate != null && !job.dateTbc
                    ? {days: {from: assignment.startDate, until: assignment.endDate ?? null}}
                    : {}),
            })
        }
    }
    for (const requirement of workerRequirements) {
        takeAssignmentsForRequirement(requirement, Math.max(requirement.quantity, 0))
    }
    for (const requirement of workerRequirements) {
        takeAssignmentsForRequirement(requirement, Number.POSITIVE_INFINITY)
    }

    for (const assignment of job.assetAssignments) {
        if (assignment.kind !== 'unique') {
            continue
        }

        const requirement = job.assetRequirements.find(
            (entry) => entry.rentalAssetTypeId === assignment.rentalAssetTypeId,
        )
        if (!requirement) {
            continue
        }

        allocations.push({
            requirementId: requirement.id,
            resourceId: assignment.rentalAssetId,
            resourceName: assignment.rentalAssetName,
            kind: 'rental_asset',
        })
    }

    return allocations
}

export function mapBoardJobToUnassignedJob(
    job: DispatchBoardJob,
    anchorDate: Date = businessCalendarDate(),
): UnassignedJob {
    const requirements = [
        ...job.workerRequirements.map((requirement) => mapBoardRequirement(requirement, 'worker')),
        ...job.assetRequirements.map((requirement) =>
            mapBoardRequirement(requirement, 'rental_asset'),
        ),
    ]
    const {scheduleKind, span} = mapJobScheduleToTimeline(
        {
            startDate: job.startDate,
            startTime: job.startTime,
            endDate: job.endDate,
            endTime: job.endTime,
        },
        anchorDate,
    )

    return {
        id: job.id,
        jobCode: job.displayId ?? formatJobCode(job.id),
        label: job.name,
        customer: job.customerName,
        customerId: job.customerId,
        dispatchYardId: job.dispatchYardId,
        ...(job.closerYardName ? {closerYardName: job.closerYardName} : {}),
        ...buildOutOfStateLabelProps(job),
        ...(job.partialPickup ? {partialPickup: job.partialPickup} : {}),
        ...(job.unconfirmedRentals?.length
            ? {unconfirmedRentalCount: job.unconfirmedRentals.length}
            : {}),
        internalContactName: job.internalContactName,
        scheduleKind,
        span,
        schedule: {
            startDate: job.startDate,
            startTime: job.startTime,
            endDate: job.endDate,
            endTime: job.endTime,
            ...(job.dateTbc ? {dateTbc: true} : {}),
        },
        requirements,
        allocations: mapBoardJobToAllocations(job),
        allocationStatus: job.allocationStatus as AllocationStatus,
        warnings: requirements.length === 0 ? ['No requirements defined'] : undefined,
        boardScenario: boardScenarioFromBoardJob(job),
    }
}

function shouldCommitToBoard(job: UnassignedJob): boolean {
    return isJobDispatched(job)
}

export function mapBoardResponseToDispatchTimelineData(
    response: DispatchBoardResponse,
): DispatchTimelineData {
    const unassignedJobs: UnassignedJob[] = []
    const assignments: DispatchAssignment[] = []
    const jobIdentities: Record<string, BoardJobIdentity> = {}
    const anchorDate = parseApiDate(response.startDate)

    for (const job of response.jobs) {
        const boardJob = mapBoardJobToUnassignedJob(job, anchorDate)
        jobIdentities[job.id] = boardJobIdentity(job)
        if (shouldCommitToBoard(boardJob)) {
            assignments.push(commitJobToTimeline(boardJob))
        } else {
            unassignedJobs.push(boardJob)
        }
    }

    const uniqueEquipment = response.rentalAssets.filter((asset) => asset.isUniquelyIdentified)

    return {
        scheduleWindowStart: response.startDate,
        scheduleWindowEnd: response.endDate,
        fieldLabel: 'FIELD OPERATIONS',
        scheduleDate: buildTodayScheduleDate(),
        timeSlots: buildWorkDaySlots(businessTimestampFromDate(new Date()).getHours()),
        resources: response.workers.map((worker) => {
            const unavailable = (response.unavailableDrivers ?? [])
                .filter((row) => row.workerId === worker.id)
                .map(({from, until, label, detail}) => ({from, until, label, detail}))
            const resource = mapWorkerToResource(worker)
            return unavailable.length > 0 ? {...resource, unavailable} : resource
        }),
        equipment: uniqueEquipment.map((asset) => {
            const out = (response.outOfServiceEquipment ?? []).find(
                (row) => row.rentalAssetId === asset.id,
            )
            const onRent = (response.unitsOnRent ?? []).find(
                (row) => row.rentalAssetId === asset.id,
            )
            const resource = {
                ...mapUniqueRentalAssetToResource(asset),
                ...(onRent
                    ? {
                          onRent: {
                              projectId: onRent.projectId,
                              order: onRent.order,
                              since: onRent.since,
                          },
                      }
                    : {}),
            }
            return out
                ? {
                      ...resource,
                      outOfService: {
                          reason: out.note ? `${out.label} · ${out.note}` : out.label,
                          expectedBack: out.expectedBack,
                      },
                  }
                : resource
        }),
        unassignedJobs,
        jobIdentities,
        assignments,
        hourlyLoad: [],
    }
}
